import axios from 'axios';

  const LOCAL_URL = 'http://localhost:35548';

  let webSerialPort = null;
  let webSerialWriter = null;
  let webSerialReader = null;
  let webSerialReadTask = null;
  let webSerialPortInfo = null;
  let webSerialReadBuffer = '';
  let webSerialOkResolver = null;
  const servoLiveDisconnectListeners = new Set();

  // Android
  const hasAndroidServoBridge = () =>
      typeof window !== 'undefined' &&
      typeof window.AndroidInterface !== 'undefined' &&
      typeof window.AndroidInterface.connectServoLive === 'function';

    
    const parseAndroidResult = value => {
      try {
          return JSON.parse(value);
      } catch (error) {
          return {success: false, error: 'Invalid Android response'};
      }
  };

  // Electron
  const isElectron = () => {
      if (typeof window !== 'undefined' && typeof window.process === 'object' && window.process.type === 'renderer') {
          return true;
      }
      if (typeof process !== 'undefined' && typeof process.versions === 'object' && !!process.versions.electron) {
          return true;
      }
      return typeof navigator === 'object' &&
          typeof navigator.userAgent === 'string' &&
          navigator.userAgent.indexOf('Electron') >= 0;
  };

  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
  const WEB_SERIAL_POST_OPEN_SETTLE_MS = 500;
  const WEB_SERIAL_START_RETRY_WINDOW_MS = 2000;
  const WEB_SERIAL_START_RETRY_INTERVAL_MS = 200;

  const writeWebSerialLine = async message => {
      if (!webSerialWriter) {
          throw new Error('Web serial writer is not ready.');
      }
      const encoder = new TextEncoder();
      await webSerialWriter.write(encoder.encode(`${message}\r`));
  };

  const notifyWebSerialOkReceived = () => {
      if (webSerialOkResolver) {
          webSerialOkResolver();
          webSerialOkResolver = null;
      }
  };

  const notifyServoLiveDisconnected = () => {
      servoLiveDisconnectListeners.forEach(listener => {
          try {
              listener();
          } catch (error) {
              console.error('servo live disconnect listener failed', error);
          }
      });
  };

  const handleWebSerialChunk = chunk => {
      if (!chunk) return;

      const chunkNormalized = chunk.trim();
      if (chunkNormalized.toLowerCase() === 'ok') {
          notifyWebSerialOkReceived();
      }

      webSerialReadBuffer += chunk;

      while (true) {
          const lineBreakIndex = webSerialReadBuffer.search(/[\r\n]/);
          if (lineBreakIndex < 0) break;

          const line = webSerialReadBuffer.slice(0, lineBreakIndex).trim();
          let deleteLength = lineBreakIndex + 1;
          if (
              webSerialReadBuffer.charAt(lineBreakIndex) === '\r' &&
              webSerialReadBuffer.charAt(deleteLength) === '\n'
          ) {
              deleteLength += 1;
          }
          webSerialReadBuffer = webSerialReadBuffer.slice(deleteLength);

          if (line.toLowerCase() === 'ok') {
              notifyWebSerialOkReceived();
          }
      }
  };

  const startWebSerialReader = async () => {
      if (!webSerialPort?.readable) {
          throw new Error('Web serial reader is not available.');
      }

      const reader = webSerialPort.readable.getReader();
      webSerialReader = reader;
      webSerialReadBuffer = '';

      try {
          const decoder = new TextDecoder();
          while (webSerialReader === reader) {
              const {value, done} = await reader.read();
              if (done) break;
              handleWebSerialChunk(decoder.decode(value, {stream: true}));
          }
      } catch (error) {
          if (error?.name !== 'AbortError') {
              console.error('servo live web: read loop failed', error);
          }
      } finally {
          if (webSerialReader === reader) {
              webSerialReader = null;
          }
          reader.releaseLock();
      }
  };

  const waitForWebSerialOkWhileSendingStart = async () => {
      const okPromise = new Promise(resolve => {
          webSerialOkResolver = resolve;
      });
      const deadline = Date.now() + WEB_SERIAL_START_RETRY_WINDOW_MS;

      try {
          while (Date.now() < deadline) {
              await writeWebSerialLine('start');

              const receivedOk = await Promise.race([
                  okPromise.then(() => true),
                  delay(WEB_SERIAL_START_RETRY_INTERVAL_MS).then(() => false)
              ]);

              if (receivedOk) {
                  return true;
              }
          }

          return false;
      } finally {
          webSerialOkResolver = null;
      }
  };

  const closeWebSerialPort = async () => {
      const portToClose = webSerialPort;

      try {
          if (webSerialWriter) {
              try {
                  await writeWebSerialLine('end');
                  await delay(50);
              } catch (error) {
                  console.warn('servo live web: failed to send end', error);
              }
          }
      } finally {
          webSerialOkResolver = null;

          if (webSerialReader) {
              try {
                  await webSerialReader.cancel();
              } catch (error) {
                  console.warn('servo live web: reader cancel failed', error);
              }
          }

          if (webSerialReadTask) {
              await webSerialReadTask;
              webSerialReadTask = null;
          }

          if (webSerialWriter) {
              webSerialWriter.releaseLock();
              webSerialWriter = null;
          }

          webSerialPort = null;

          if (portToClose) {
              try {
                  await portToClose.close();
              } catch (error) {
                  if (error?.name !== 'InvalidStateError') {
                      throw error;
                  }
              }
          }

          webSerialReader = null;
          webSerialPortInfo = null;
          webSerialReadBuffer = '';
      }
  };

  const handleWebSerialDisconnect = async event => {
      if (!webSerialPort) return;
      if (event?.target && event.target !== webSerialPort) return;

      await closeWebSerialPort();
      notifyServoLiveDisconnected();
  };

  const normalizeServoLiveMessage = message => {
      const trimmed = String(message || '').trim();
      const upper = trimmed.toUpperCase();

      if (upper === 'START' || upper === 'END') {
          return trimmed;
      }

      const match = trimmed.match(/^S(\d+),(\d+)(?:,(\d+))?$/i);
      if (!match) return trimmed;

      const [, port, angle] = match;
      return `S${port},${angle},6`;
  };

  export const connectServoLive = async portPath => {
      if (isElectron()) {
          const response = await axios.post(`${LOCAL_URL}/connectPort`, {
              port: portPath
          });
          return response.data;
      }

      if (hasAndroidServoBridge()) {
        const result = window.AndroidInterface.connectServoLive();
        return parseAndroidResult(result);
      }

      if (!('serial' in navigator)) {
          throw new Error('Web Serial API is not supported in this browser.');
      }

      if (webSerialPort && webSerialWriter) {
          console.log('servo live already connected');
          return {success: true, port: webSerialPortInfo};
      }

      const port = await navigator.serial.requestPort();
      console.log('servo live web: port selected');

      await port.open({baudRate: 115200});
      console.log('servo live web: port opened');

      const writer = port.writable.getWriter();

      webSerialPort = port;
      webSerialWriter = writer;
      webSerialPortInfo = 'web-serial';

      webSerialReadTask = startWebSerialReader();

      await delay(WEB_SERIAL_POST_OPEN_SETTLE_MS);

      const connected = await waitForWebSerialOkWhileSendingStart();
      if (!connected) {
          await closeWebSerialPort();
          throw new Error('Web serial handshake failed: no ok response.');
      }

      return {success: true, port: webSerialPortInfo};
  };

  export const disconnectServoLive = async () => {
      if (isElectron()) {
          const response = await axios.post(`${LOCAL_URL}/disconnectPort`);
          return response.data;
      }

      if (hasAndroidServoBridge()) {
        const result = window.AndroidInterface.disconnectServoLive();
        return parseAndroidResult(result);
      }

      if (!webSerialPort) {
          return {success: true};
      }

      await closeWebSerialPort();

      return {success: true};
  };

  export const writeServoLive = async message => {
      const normalizedMessage = normalizeServoLiveMessage(message);
      console.log('writeServoLive:', {
          normalizedMessage,
          isElectron: isElectron(),
          hasPort: Boolean(webSerialPort),
          hasWriter: Boolean(webSerialWriter)
      });

      if (isElectron()) {
          const response = await axios.post(`${LOCAL_URL}/serialWrite`, {message: normalizedMessage});
          return response.data;
      }

      if (hasAndroidServoBridge()) {
        const result = window.AndroidInterface.writeServoLive(normalizedMessage);
        return parseAndroidResult(result);
      }

      await writeWebSerialLine(normalizedMessage);
      return {success: true};
  };

  export const getServoLiveStatus = async () => {
      if (isElectron()) {
          const response = await axios.get(`${LOCAL_URL}/livePortStatus`);
          return {
              mode: 'electron',
              ...response.data
          };
      }

      if(hasAndroidServoBridge()) {
        const result = window.AndroidInterface.getServoLiveStatus();
        return {
            mode: 'android',
            ...parseAndroidResult(result)
        };
      }

      return {
          mode: 'web',
          connected: Boolean(webSerialPort && webSerialWriter),
          port: webSerialPortInfo
      };
  };

export const listServoLivePorts = async () => {
    if (isElectron()) {
        const response = await axios.get(`${LOCAL_URL}/getPorts`);
        return response.data?.ports || [];
    }

    if (!'serial' in navigator || !navigator.serial.getPorts) {
        return [];
    }

    const ports = await navigator.serial.getPorts();
    return ports.map((port, index) => ({
          path: `web-serial-${index + 1}`,
          webSerialPort: port,
          manufacturer: 'Web Serial',
          serialNumber: 'N/A',
          vendorId: 'N/A',
          productId: 'N/A'
      }));
}

export const addServoLiveDisconnectListener = listener => {
    servoLiveDisconnectListeners.add(listener);
    return () => {
        servoLiveDisconnectListeners.delete(listener);
    };
};

if (typeof navigator !== 'undefined' && navigator.serial?.addEventListener) {
    navigator.serial.addEventListener('disconnect', event => {
        void handleWebSerialDisconnect(event);
    });
}
